import { useMemo } from "react";
import { Box, Divider, Skeleton, Typography, styled } from "@mui/material";

import { CompoundedPrescriptionDetails } from "../../../interfaces";
import { theme } from "../../../config";
import { getUnitForQuantity } from "../../../utils";

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  border: "1px solid #eaeaea",
  borderRadius: "8px",
  backgroundColor: theme.pallete.background.white,
  gap: "4px",
  padding: "8px 16px",
});

const Row = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
});

const InfoColumn = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minWidth: 0,
  gap: "4px",
});

const ActionColumn = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "flex-start",
  flexShrink: 0,
});

const ProductName = styled(Typography)({
  fontWeight: 500,
  color: theme.pallete.text.black,
  textWrap: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const ProductDetails = styled(Typography)({
  fontSize: "14px",
  color: theme.pallete.text.gray,
});

const CompoundedIngredients = styled(ProductDetails)({
  color: theme.pallete.text.black,
  whiteSpace: "pre-line",
});

const TreatmentDetail = styled(Typography)({
  fontSize: "14px",
  color: theme.pallete.text.gray,
});

interface PrescriptionDetailCardProps {
  details: CompoundedPrescriptionDetails;
}

export function CompoundPrescriptionDetailCard({
  details,
}: PrescriptionDetailCardProps) {
  const {
    compoundedIngredients,
    posologyQuantity,
    posologyFormat,
    frequencyQuantity,
    frequencyPeriod,
    durationQuantity,
    durationPeriod,
    indications,
  } = details;

  const consumptionDetail = useMemo(() => {
    if (posologyQuantity && posologyFormat) {
      return `${posologyQuantity} ${getUnitForQuantity(
        posologyFormat,
        posologyQuantity,
      )} cada ${frequencyQuantity} ${getUnitForQuantity(
        frequencyPeriod,
        frequencyQuantity,
      )}`;
    }
    return "";
  }, [posologyQuantity, posologyFormat, frequencyQuantity, frequencyPeriod]);

  const treatmentDuration = useMemo(() => {
    if (durationQuantity && durationPeriod) {
      return `Tratamiento por ${durationQuantity} ${getUnitForQuantity(
        durationPeriod,
        durationQuantity,
      )}`;
    }
    return "";
  }, [durationQuantity, durationPeriod]);

  return (
    <Container>
      <Row>
        <InfoColumn>
          <CompoundedIngredients>{compoundedIngredients}</CompoundedIngredients>
          <TreatmentDetail>{consumptionDetail}</TreatmentDetail>
          <TreatmentDetail>{treatmentDuration}</TreatmentDetail>
        </InfoColumn>
      </Row>
      {indications && (
        <>
          <Divider />
          <TreatmentDetail>{indications}</TreatmentDetail>
        </>
      )}
    </Container>
  );
}

CompoundPrescriptionDetailCard.Skeleton =
  function CompoundPrescriptionDetailCardSkeleton() {
    return (
      <Container>
        <Row>
          <InfoColumn>
            <ProductName>
              <Skeleton variant="text" width="90%" />
            </ProductName>
            <ProductDetails>
              <Skeleton variant="text" width="90%" />
            </ProductDetails>
            <TreatmentDetail>
              <Skeleton variant="text" width="90%" />
            </TreatmentDetail>
          </InfoColumn>
          <ActionColumn></ActionColumn>
        </Row>
      </Container>
    );
  };
